import { RetrieveDocumentGenerationJob } from '../../../../api';
import { isActiveJobStatus } from '../../../../pages/DocumentGenerationJobs/models/jobStatus';

export type ParsingProgressState = 'queued' | 'parsing' | 'preparing' | 'ready' | 'failed';

export const PARSING_PROGRESS_COPY: Record<ParsingProgressState, string> = {
  queued: 'Queued for citation processing',
  parsing: 'Parsing PDF for citations...',
  preparing: 'Preparing citation data...',
  ready: 'Ready for citations',
  failed: 'Citation processing failed',
};

/** Only a job that is still running can stand in for the one this tab tried to create. */
export const isAdoptableParsingStatus = isActiveJobStatus;

export const isParsingJob = (job: Pick<RetrieveDocumentGenerationJob, 'processing_type'> | null | undefined): boolean =>
  job?.processing_type === RetrieveDocumentGenerationJob.processing_type.PARSING;

/** File id and company id are enough; referenced_in is not required. */
export const canStartCitableParse = (fileId?: number | null, companyId?: string | null): boolean =>
  Boolean(fileId && companyId);

/** Only a still-running PARSING job is already in flight. Cap-table jobs never count. */
export const isInFlightParsingJob = (job: RetrieveDocumentGenerationJob | null | undefined): boolean =>
  isParsingJob(job) && isAdoptableParsingStatus(job?.status);

export const jobMatchesDocument = (
  job: RetrieveDocumentGenerationJob,
  documentReferenceId: number,
  fileGuid?: string | null
): boolean => {
  if (!isParsingJob(job)) {
    return false;
  }
  const metaId = job.metadata?.document_reference_id;
  if (metaId != null && Number(metaId) === Number(documentReferenceId)) {
    return true;
  }
  if (!fileGuid) {
    return false;
  }
  return (job.reference_files ?? []).some(file => file.guid === fileGuid);
};

const jobTime = (job: RetrieveDocumentGenerationJob): number => Date.parse(job.updated_at || job.created_at || '') || 0;

const newestJob = (jobs: RetrieveDocumentGenerationJob[]): RetrieveDocumentGenerationJob | null =>
  jobs.length === 0 ? null : [...jobs].sort((left, right) => jobTime(right) - jobTime(left))[0];

/** Newest in-flight PARSING job, else the newest terminal PARSING job. */
export const adoptLatestParsingJob = (
  jobs: RetrieveDocumentGenerationJob[],
  documentReferenceId: number,
  fileGuid?: string | null
): RetrieveDocumentGenerationJob | null => {
  const matches = jobs.filter(job => jobMatchesDocument(job, documentReferenceId, fileGuid));
  return newestJob(matches.filter(isInFlightParsingJob)) ?? newestJob(matches);
};

export const parsingProgressState = (
  job: RetrieveDocumentGenerationJob | null | undefined,
  parserReady: boolean
): ParsingProgressState => {
  if (parserReady) {
    return 'ready';
  }
  if (!job) {
    return 'queued';
  }
  if (job.status === RetrieveDocumentGenerationJob.status.FAILED) {
    return 'failed';
  }
  if (job.status === RetrieveDocumentGenerationJob.status.PENDING) {
    return 'queued';
  }
  if (job.status === RetrieveDocumentGenerationJob.status.IN_PROGRESS) {
    return 'parsing';
  }
  return 'preparing';
};

export const PARSING_JOBS_PAGE_SIZE = 50;

export type ParsingJobDiscovery = {
  adopted: RetrieveDocumentGenerationJob | null;
  listFailed: boolean;
};

/**
 * POST a CITABLE PARSING job whenever this PDF still has no parser JSON
 * and nothing is already parsing it. A stamped json_file_path is materializing,
 * not ready: do not POST while that fetch is in flight. List failures and stale
 * COMPLETED/FAILED (or cap-table) jobs must not suppress the fallback.
 */
export const shouldAutoCreateParsingJob = ({
  parserReady,
  adoptedJob,
  parserArtifactPresent = false,
}: {
  parserReady: boolean;
  adoptedJob: RetrieveDocumentGenerationJob | null;
  parserArtifactPresent?: boolean;
}): boolean => !parserReady && !parserArtifactPresent && !isInFlightParsingJob(adoptedJob);

export const isParserJsonReady = (data: unknown): data is unknown[] => Array.isArray(data);

export type CitationParseDisplay = 'nothing' | 'preparing' | 'failed';

export const shouldShowPreparingBanner = (display: CitationParseDisplay, parserReady: boolean): boolean =>
  !parserReady && display === 'preparing';

/** Company jobs list is a bare array at runtime; generated client types a paginated page. */
export const jobsFromListResponse = (response: unknown): RetrieveDocumentGenerationJob[] => {
  if (Array.isArray(response)) {
    return response as RetrieveDocumentGenerationJob[];
  }
  if (response && typeof response === 'object' && Array.isArray((response as { results?: unknown }).results)) {
    return (response as { results: RetrieveDocumentGenerationJob[] }).results;
  }
  return [];
};

export const resolveCitationParseDisplay = ({
  parserReady,
  parserJsonError,
  parserResultsError,
  parserArtifactPresent,
  parseFailed,
  parseBusy,
  jobInFlight,
}: {
  parserReady: boolean;
  parserJsonError: boolean;
  parserResultsError: boolean;
  parserArtifactPresent: boolean;
  parseFailed: boolean;
  parseBusy: boolean;
  jobInFlight: boolean;
}): CitationParseDisplay => {
  if (parserReady) {
    return 'nothing';
  }
  if (parserJsonError || parserResultsError || (parseFailed && !jobInFlight)) {
    return 'failed';
  }
  if (parseBusy || jobInFlight || parserArtifactPresent) {
    return 'preparing';
  }
  return 'nothing';
};

export const discoverParsingJob = async ({
  fetchPage,
  documentReferenceId,
  fileGuid,
  pageSize = PARSING_JOBS_PAGE_SIZE,
}: {
  fetchPage: (page: number, size: number) => Promise<unknown>;
  documentReferenceId: number;
  fileGuid?: string | null;
  pageSize?: number;
}): Promise<ParsingJobDiscovery> => {
  let bestInFlight: RetrieveDocumentGenerationJob | null = null;
  let bestTerminal: RetrieveDocumentGenerationJob | null = null;
  try {
    for (let page = 0; page < 100; page += 1) {
      // Pages are sequential: a later page is older, and a short page is the end.
      // eslint-disable-next-line no-await-in-loop
      const jobs = jobsFromListResponse(await fetchPage(page, pageSize));
      for (let i = 0; i < jobs.length; i += 1) {
        const job = jobs[i];
        if (jobMatchesDocument(job, documentReferenceId, fileGuid)) {
          if (isInFlightParsingJob(job)) {
            if (!bestInFlight || jobTime(job) > jobTime(bestInFlight)) {
              bestInFlight = job;
            }
          } else if (!bestTerminal || jobTime(job) > jobTime(bestTerminal)) {
            bestTerminal = job;
          }
        }
      }
      if (bestInFlight) {
        return { adopted: bestInFlight, listFailed: false };
      }
      if (jobs.length < pageSize) {
        return { adopted: bestTerminal, listFailed: false };
      }
    }
    return { adopted: bestTerminal, listFailed: false };
  } catch {
    return { adopted: null, listFailed: true };
  }
};

export const isFailedParsingStatus = (status: RetrieveDocumentGenerationJob.status | undefined): boolean =>
  status === RetrieveDocumentGenerationJob.status.FAILED;

export const HTTP_CONFLICT = 409;

/** Backoff before each re-POST while another request holds this document's PARSING lock. */
export const PARSING_CONFLICT_RETRY_DELAYS_IN_MS = [1000, 2000, 4000];

/** HTTP status carried by a thrown ApiError, when the throw has one. */
export const errorStatusCode = (error: unknown): number | undefined => {
  const thrown = error as { status?: unknown; response?: { status?: unknown } } | null | undefined;
  const status = thrown?.status ?? thrown?.response?.status;
  return typeof status === 'number' ? status : undefined;
};

/** The backend rejects a PARSING POST with 409 while a concurrent request owns the same document. */
export const isParsingConflictError = (error: unknown): boolean => errorStatusCode(error) === HTTP_CONFLICT;

export type ParsingJobCreateOutcome =
  | { outcome: 'created'; job: RetrieveDocumentGenerationJob }
  | { outcome: 'adopted'; job: RetrieveDocumentGenerationJob }
  | { outcome: 'gave-up'; attempts: number };

/**
 * Creates the PARSING job, resolving a 409 by adopting the job the winning request made
 * or re-POSTing on a fixed backoff. Non-409 errors are rethrown untouched.
 * `wait` returns false when the caller went away, which ends the retries.
 */
export const createParsingJobWithConflictRetry = async ({
  postJob,
  findExistingJob,
  wait,
}: {
  postJob: () => Promise<RetrieveDocumentGenerationJob>;
  findExistingJob: () => Promise<RetrieveDocumentGenerationJob | null>;
  wait: (delayInMs: number) => Promise<boolean>;
}): Promise<ParsingJobCreateOutcome> => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return { outcome: 'created', job: await postJob() };
    } catch (error) {
      if (!isParsingConflictError(error)) {
        throw error;
      }
      // eslint-disable-next-line no-await-in-loop
      const existing = await findExistingJob();
      if (existing && isAdoptableParsingStatus(existing.status)) {
        return { outcome: 'adopted', job: existing };
      }
      const delayInMs = PARSING_CONFLICT_RETRY_DELAYS_IN_MS[attempt];
      // eslint-disable-next-line no-await-in-loop
      if (delayInMs == null || !(await wait(delayInMs))) {
        return { outcome: 'gave-up', attempts: attempt + 1 };
      }
    }
  }
};
